import { useState } from "react";

/* ── Review queue data ── */
interface ReviewItem {
  id: string;
  variable_name: string;
  resolution: "data_match" | "proxy" | "correlation" | "synthetic";
  expert_name: string;
  source_quote: string;
  mechanism: string;
  certainty: number;
  prior_mean: number;
  prior_std: number;
  matched_detail: string; // what the resolution found
  submitted_at: string;
  status: "pending" | "approved" | "rejected" | "needs_shelf";
  reviewer_notes: string;
}

const DEMO_QUEUE: ReviewItem[] = [
  {
    id: "rev_001",
    variable_name: "Parental_Involvement_Score",
    resolution: "synthetic",
    expert_name: "Sarah Hartwell",
    source_quote: "Parental involvement is the single strongest predictor of favorable custody outcomes.",
    mechanism: "Active parenting correlates with favorable custody outcomes through consistent visitation and engagement.",
    certainty: 0.82,
    prior_mean: 0.40,
    prior_std: 0.12,
    matched_detail: "No dataset match. Synthetic generation from expert prior N(0.400, 0.120). SHELF completed — 3 experts converged (Round 2 spread: 0.41).",
    submitted_at: "2026-03-15 11:02",
    status: "approved",
    reviewer_notes: "Strong expert consensus. SHELF convergence good. Deploy to model.",
  },
  {
    id: "rev_002",
    variable_name: "Domestic_Violence_Risk",
    resolution: "correlation",
    expert_name: "Dr. Diana Washington",
    source_quote: "Any documented DV history shifts the calculus entirely — nearly deterministic.",
    mechanism: "DV triggers mandatory safety evaluations and supervised visitation requirements.",
    certainty: 0.91,
    prior_mean: 0.65,
    prior_std: 0.08,
    matched_detail: "Top correlate: Study_Hours (r=0.401). No direct match but strong signal in residuals. Expert certainty very high (91%).",
    submitted_at: "2026-03-15 14:35",
    status: "approved",
    reviewer_notes: "High certainty, clear mechanism. Approved for posterior update.",
  },
  {
    id: "rev_003",
    variable_name: "Child_Attachment_Quality",
    resolution: "synthetic",
    expert_name: "Dr. Michael Kwan",
    source_quote: "Attachment security predicts long-term adjustment. Securely attached children show 2.5x better outcomes.",
    mechanism: "Secure attachment mediates child wellbeing through emotional regulation capacity.",
    certainty: 0.74,
    prior_mean: 0.55,
    prior_std: 0.15,
    matched_detail: "No dataset match. Synthetic viable from prior N(0.550, 0.150). SHELF not yet completed — only 1 expert estimate.",
    submitted_at: "2026-03-16 11:30",
    status: "needs_shelf",
    reviewer_notes: "Need 2 more expert estimates before aggregating prior. Route to SHELF.",
  },
  {
    id: "rev_004",
    variable_name: "Income_Disparity_Index",
    resolution: "data_match",
    expert_name: "James Thornton",
    source_quote: "When income disparity exceeds 3:1, the lower-earning spouse faces systematic disadvantage.",
    mechanism: "Financial asymmetry affects legal representation quality and negotiation power.",
    certainty: 0.68,
    prior_mean: 0.35,
    prior_std: 0.18,
    matched_detail: "Partial match: 'Household_Income' in dataset (token overlap: 33%). Could derive disparity index from income column if both-parent records exist.",
    submitted_at: "2026-03-14 16:00",
    status: "pending",
    reviewer_notes: "",
  },
  {
    id: "rev_005",
    variable_name: "Geographic_Stability",
    resolution: "proxy",
    expert_name: "Hon. Rebecca Paulson",
    source_quote: "A parent who's moved 3 times in 2 years raises red flags.",
    mechanism: "Residential stability signals environment consistency for schooling and social development.",
    certainty: 0.60,
    prior_mean: 0.30,
    prior_std: 0.20,
    matched_detail: "Proxy cluster: [Sleep_Quality, Class_Attendance] (corr > 0.7). Weak semantic match but statistical structure suggests shared latent factor.",
    submitted_at: "2026-03-17 09:20",
    status: "pending",
    reviewer_notes: "",
  },
  {
    id: "rev_006",
    variable_name: "Substance_Abuse_History",
    resolution: "synthetic",
    expert_name: "Dr. Adriana Morales",
    source_quote: "Active substance abuse is disqualifying, but recovery history of 2+ years is viewed favorably.",
    mechanism: "Substance abuse directly impairs parenting capacity and child safety.",
    certainty: 0.88,
    prior_mean: 0.60,
    prior_std: 0.10,
    matched_detail: "No dataset match. Synthetic from prior N(0.600, 0.100). SHELF completed — high agreement across 3 experts.",
    submitted_at: "2026-03-13 17:10",
    status: "approved",
    reviewer_notes: "Clear cut. High certainty, strong expert agreement, well-defined mechanism.",
  },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending:     { label: "Pending Review",  color: "#f59e0b", bg: "rgba(245,158,11,0.08)", icon: "?" },
  approved:    { label: "Approved",        color: "#10b981", bg: "rgba(16,185,129,0.08)", icon: "✓" },
  rejected:    { label: "Rejected",        color: "#ef4444", bg: "rgba(239,68,68,0.08)",  icon: "×" },
  needs_shelf: { label: "Needs SHELF",     color: "#8b5cf6", bg: "rgba(139,92,246,0.08)", icon: "!" },
};

const RES_CONFIG: Record<string, { label: string; color: string }> = {
  data_match:  { label: "Data Match",  color: "#2563eb" },
  proxy:       { label: "Proxy",       color: "#7c3aed" },
  correlation: { label: "Correlation", color: "#0891b2" },
  synthetic:   { label: "Synthetic",   color: "#059669" },
};

/* ── Checklist ── */
function ReviewChecklist({ item }: { item: ReviewItem }) {
  const checks = [
    { label: "Expert certainty ≥ 60%", pass: item.certainty >= 0.6 },
    { label: "Mechanism documented", pass: !!item.mechanism },
    { label: "Prior parameters specified", pass: item.prior_mean > 0 && item.prior_std > 0 },
    { label: "Source quote captured", pass: !!item.source_quote },
    { label: "SHELF elicitation complete", pass: item.status !== "needs_shelf" },
    { label: "Resolution path selected", pass: !!item.resolution },
  ];
  const passCount = checks.filter(c => c.pass).length;

  return (
    <div style={{
      padding: "12px 14px", borderRadius: 8,
      background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
        <span>Quality Checklist</span>
        <span style={{ color: passCount === checks.length ? "#10b981" : "#f59e0b" }}>
          {passCount}/{checks.length}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {checks.map(check => (
          <div key={check.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
            <div style={{
              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: check.pass ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.1)",
            }}>
              {check.pass ? (
                <svg width="10" height="10" viewBox="0 0 10 10"><polyline points="2,5 4.5,7.5 8,2.5" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 10 10"><line x1="3" y1="3" x2="7" y2="7" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" /><line x1="7" y1="3" x2="3" y2="7" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" /></svg>
              )}
            </div>
            <span style={{ color: check.pass ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)" }}>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function VariableReview() {
  const [items, setItems] = useState<ReviewItem[]>(DEMO_QUEUE);
  const [expandedId, setExpandedId] = useState<string | null>("rev_004");
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const filtered = filterStatus ? items.filter(i => i.status === filterStatus) : items;
  const pendingCount = items.filter(i => i.status === "pending").length;
  const shelfCount = items.filter(i => i.status === "needs_shelf").length;
  const approvedCount = items.filter(i => i.status === "approved").length;

  const updateStatus = (id: string, status: ReviewItem["status"], notes: string = "") => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, status, reviewer_notes: notes || item.reviewer_notes } : item
    ));
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
      }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Variable Review
          {pendingCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>
              {pendingCount} pending
            </span>
          )}
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Quality gate — approve variables before they enter the registry
        </p>
      </div>

      {/* Status filter */}
      <div style={{ padding: "12px 32px", display: "flex", gap: 8, flexShrink: 0 }}>
        {(["pending", "needs_shelf", "approved", "rejected"] as const).map(status => {
          const cfg = STATUS_CONFIG[status];
          const count = items.filter(i => i.status === status).length;
          const isActive = filterStatus === status;
          return (
            <button key={status} onClick={() => setFilterStatus(isActive ? null : status)} style={{
              padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 8,
              background: isActive ? cfg.bg : "rgba(0,0,0,0.03)",
              transition: "all 0.15s ease",
            }}>
              <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>
                {count}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, color: isActive ? cfg.color : "rgba(0,0,0,0.4)" }}>
                {cfg.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Review queue */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 40px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(item => {
            const isExpanded = expandedId === item.id;
            const statusCfg = STATUS_CONFIG[item.status];
            const resCfg = RES_CONFIG[item.resolution];

            return (
              <div key={item.id} style={{
                border: `1.5px solid ${isExpanded ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.06)"}`,
                borderRadius: 12, overflow: "hidden", background: "#fff",
                boxShadow: isExpanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
                transition: "all 0.2s ease",
              }}>
                {/* Header row */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  style={{
                    padding: "14px 18px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 14,
                    background: item.status === "pending" ? "rgba(245,158,11,0.03)" : "transparent",
                  }}
                >
                  {/* Status icon */}
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: statusCfg.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 800, color: statusCfg.color,
                  }}>
                    {statusCfg.icon}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{item.variable_name}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                        background: resCfg.color + "14", color: resCfg.color,
                      }}>{resCfg.label}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>
                      {item.expert_name} · {item.submitted_at}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                      background: statusCfg.bg, color: statusCfg.color,
                    }}>{statusCfg.label}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                      style={{ opacity: 0.25, transition: "transform 0.2s ease", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                      {/* Left: details */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {/* Quote */}
                        <div style={{
                          padding: "10px 14px", borderRadius: 8,
                          borderLeft: "3px solid rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)",
                        }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 4 }}>Expert Quote</div>
                          <div style={{ fontSize: 11.5, lineHeight: 1.55, fontStyle: "italic", color: "rgba(0,0,0,0.65)" }}>
                            "{item.source_quote}"
                          </div>
                        </div>

                        {/* Mechanism */}
                        <div style={{
                          padding: "10px 14px", borderRadius: 8,
                          borderLeft: `3px solid ${resCfg.color}40`, background: resCfg.color + "06",
                        }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: resCfg.color, marginBottom: 4 }}>Mechanism</div>
                          <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "rgba(0,0,0,0.65)" }}>
                            {item.mechanism}
                          </div>
                        </div>

                        {/* Resolution detail */}
                        <div style={{
                          padding: "10px 14px", borderRadius: 8,
                          background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)",
                        }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.3)", marginBottom: 4 }}>Resolution Detail</div>
                          <div style={{ fontSize: 11, lineHeight: 1.55, color: "rgba(0,0,0,0.55)" }}>
                            {item.matched_detail}
                          </div>
                        </div>

                        {/* Prior params */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          {[
                            ["Prior μ", item.prior_mean.toFixed(3)],
                            ["Prior σ", item.prior_std.toFixed(3)],
                            ["Certainty", `${(item.certainty * 100).toFixed(0)}%`],
                          ].map(([label, val]) => (
                            <div key={label as string} style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.03)", textAlign: "center" }}>
                              <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
                              <div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{val}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right: checklist + actions */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <ReviewChecklist item={item} />

                        {/* Reviewer notes */}
                        {item.reviewer_notes && (
                          <div style={{
                            padding: "10px 14px", borderRadius: 8,
                            background: statusCfg.bg, border: `1px solid ${statusCfg.color}20`,
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: statusCfg.color, marginBottom: 4 }}>Reviewer Notes</div>
                            <div style={{ fontSize: 11, lineHeight: 1.5, color: "rgba(0,0,0,0.6)" }}>
                              {item.reviewer_notes}
                            </div>
                          </div>
                        )}

                        {/* Action buttons */}
                        {item.status === "pending" && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: "auto" }}>
                            <button
                              onClick={() => updateStatus(item.id, "approved", "Reviewed and approved. Quality checks pass.")}
                              style={{
                                padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                                background: "#10b981", color: "#fff", fontSize: 12, fontWeight: 700,
                                transition: "all 0.15s ease",
                              }}
                            >
                              Approve → Send to Registry
                            </button>
                            <button
                              onClick={() => updateStatus(item.id, "needs_shelf", "Needs additional expert estimates via SHELF.")}
                              style={{
                                padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                                background: "#8b5cf6", color: "#fff", fontSize: 12, fontWeight: 700,
                                transition: "all 0.15s ease",
                              }}
                            >
                              Route to SHELF → Need More Experts
                            </button>
                            <button
                              onClick={() => updateStatus(item.id, "rejected", "Insufficient evidence or unclear mechanism.")}
                              style={{
                                padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                                background: "rgba(0,0,0,0.06)", color: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 600,
                                transition: "all 0.15s ease",
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        )}

                        {item.status === "needs_shelf" && (
                          <button
                            onClick={() => updateStatus(item.id, "pending", "SHELF completed. Ready for re-review.")}
                            style={{
                              padding: "10px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                              background: "#8b5cf6", color: "#fff", fontSize: 12, fontWeight: 700,
                              marginTop: "auto",
                            }}
                          >
                            Mark SHELF Complete → Re-Review
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
